import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    galleryId: String,
    currentSlide: { type: Number, default: 0 },
    totalSlides: { type: Number, default: 0 }
  }

  static targets = ["slide", "navigation"]

  connect() {
    this.setupKeyboardNavigation()
    this.setupSlideCount()
    this.setupFocusManagement()
    this.createSlideAnnouncer()
    this.announceSlideChange()
    this.startAutoPlay()
  }

  setupKeyboardNavigation() {
    this.element.setAttribute('tabindex', '0')
    this.element.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  setupSlideCount() {
    this.totalSlidesValue = this.slideTargets.length
  }

  createSlideAnnouncer() {
    if (!this.element.querySelector('.slide-announcer')) {
      const announcer = document.createElement('div')
      announcer.className = 'slide-announcer sr-only'
      announcer.setAttribute('aria-live', 'polite')
      announcer.setAttribute('aria-atomic', 'true')
      this.element.appendChild(announcer)
    }
  }

  handleKeydown(event) {
    if (document.activeElement !== this.element && 
        !this.element.contains(document.activeElement)) {
      return
    }

    switch(event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        this.previousSlide()
        break

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        this.nextSlide()
        break

      case 'Home':
        event.preventDefault()
        this.goToSlide(0)
        break

      case 'End':
        event.preventDefault()
        this.goToSlide(this.totalSlidesValue - 1)
        break

      case ' ':
      case 'Enter':
        event.preventDefault()
        this.toggleAutoPlay()
        break

      case 'Escape':
        event.preventDefault()
        this.pauseAutoPlay()
        break
    }
  }
  
  previousSlide() {
    const newSlide = this.currentSlideValue <= 0 
      ? this.totalSlidesValue - 1 
      : this.currentSlideValue - 1
    this.goToSlide(newSlide)
  }

  nextSlide() {
    const newSlide = this.currentSlideValue >= this.totalSlidesValue - 1 
      ? 0 
      : this.currentSlideValue + 1
    this.goToSlide(newSlide)
  }

  goToSlideFromButton(event) {
    const slideIndex = parseInt(event.currentTarget.dataset.slide)
    this.goToSlide(slideIndex)
  }

  goToSlide(slideIndex) {
    if (slideIndex < 0 || slideIndex >= this.totalSlidesValue) return


    this.slideTargets.forEach((slide, index) => {
      const isActive = index === slideIndex
      slide.style.display = isActive ? 'block' : 'none'
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true')
      slide.classList.toggle('active', isActive)
    })

    this.currentSlideValue = slideIndex

    this.updateNavigationButtons()

    this.announceSlideChange()
  }

  updateNavigationButtons() {
    const dots = this.element.querySelectorAll('.carousel-dot')
    dots.forEach((dot, index) => {
      const isActive = index === this.currentSlideValue
      dot.setAttribute('aria-pressed', isActive.toString())
      dot.classList.toggle('active', isActive)
    })

    const prevButton = this.element.querySelector('.carousel-prev')
    const nextButton = this.element.querySelector('.carousel-next')

    if (prevButton) {
      prevButton.setAttribute('aria-disabled', (this.currentSlideValue === 0).toString())
    }
    if (nextButton) {
      nextButton.setAttribute('aria-disabled', 
        (this.currentSlideValue === this.totalSlidesValue - 1).toString())
    }
  }

  announceSlideChange() {
    const announcer = this.element.querySelector('.slide-announcer')

    if (!announcer) {
      this.createSlideAnnouncer()
      return
    }

    const currentSlide = this.currentSlideValue + 1
    const totalSlides = this.totalSlidesValue

    if (this.slideTargets[this.currentSlideValue]) {
      const slideImage = this.slideTargets[this.currentSlideValue].querySelector('img')
      const altText = slideImage.alt || 'Image'

      announcer.textContent = `Slide ${currentSlide} of ${totalSlides}: ${altText}`
    }
  }

  setupFocusManagement() {
    this.element.addEventListener('focus', () => {
      const currentSlideImg = this.slideTargets[this.currentSlideValue].querySelector('img')
      if (currentSlideImg) {
        currentSlideImg.focus()
      }
    })
  }

  toggleAutoPlay() {
    if (this.autoPlayInterval) {
      this.pauseAutoPlay()
    } else {
      this.startAutoPlay()
    }
  }

  startAutoPlay() {
    this.autoPlayInterval = setInterval(() => {
      this.nextSlide()
    }, 5000)

    const currentLabel = this.element.getAttribute('aria-label')
    this.element.setAttribute('aria-label', currentLabel + ' (auto-playing)')
  }

  pauseAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval)
      this.autoPlayInterval = null
    }

    const label = this.element.getAttribute('aria-label')
    this.element.setAttribute('aria-label', label.replace(' (auto-playing)', ''))
  }

  disconnect() {
    this.pauseAutoPlay()
    this.element.removeEventListener('keydown', this.handleKeydown)
  }
}
